import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import RenameDialog from './RenameDialog';

interface RenameCycleButtonProps {
  cycle: ReviewCycle;
  canRename: boolean;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onComplete: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * Phase 8C remainder — admin / submission-manager action to rename a cycle.
 *
 * Updates contentItem.name and/or contentItem.type / .audience. For
 * file-based cycles, the SharePoint file is also renamed to its new
 * canonical form. The lineage link (previousVersion) is left intact.
 *
 * Use case: typo or wrong tag in metadata — admin fixes it in place
 * without having to delete + resubmit. For forking a wrongly-matched
 * lineage, use the Fork lineage button instead (rename + unlink in one
 * atomic action).
 */
const RenameCycleButton: React.FC<RenameCycleButtonProps> = ({ cycle, canRename, getAuthHeaders, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  if (!canRename) return null;

  const handleSubmit = async (changes: {
    newName?: string;
    newType?: string;
    newAudience?: string;
    newPublicName?: string;
    newChannel?: string;
    newSourceLink?: string;
    newNoSourceReason?: string;
    newTopic?: string;
    newGeography?: string;
    reason: string;
  }) => {
    const headers = await getAuthHeaders();
    setWarning(null);

    // Walk fix 2026-08-12 (Fredrik) — this dialog now carries the same
    // fields as the dashboard's Edit Content Details, but the two
    // operations stay distinct under the hood: name/type/audience go
    // through the AUDITED rename endpoint (reason logged, SharePoint
    // canonical filename rewritten); public name/channel are plain
    // metadata (channel re-route is format-guarded server-side).
    const renameTouched = changes.newName !== undefined ||
      changes.newType !== undefined || changes.newAudience !== undefined;
    if (renameTouched) {
      // Backend rename endpoint requires newName explicitly even if the
      // change is only type/audience — send the current name when the
      // user only touched the dropdowns.
      const body: Record<string, string> = {
        newName: changes.newName ?? cycle.contentItem.name ?? '',
        reason: changes.reason
      };
      if (changes.newType) body.newType = changes.newType;
      if (changes.newAudience) body.newAudience = changes.newAudience;
      const res = await axios.patch(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/rename`,
        body,
        { headers }
      );
      if (res.data?.sharePointRenameError) {
        setWarning(`Renamed in CellcoHub but SharePoint rename failed: ${res.data.sharePointRenameError}`);
      }
    }

    if (changes.newPublicName !== undefined || changes.newChannel !== undefined ||
        changes.newSourceLink !== undefined || changes.newNoSourceReason !== undefined ||
        changes.newTopic !== undefined || changes.newGeography !== undefined) {
      const meta: Record<string, string | null> = {};
      if (changes.newPublicName !== undefined) meta.publicName = changes.newPublicName;
      if (changes.newChannel !== undefined) meta.channel = changes.newChannel;
      // 14e walk station 4 — source late-attach rides the same metadata
      // PUT; the server re-runs the D81 gate on the merged result. Empty
      // string means "clear" (backend normalizes falsy → null).
      if (changes.newSourceLink !== undefined) meta.sourceLink = changes.newSourceLink || null;
      if (changes.newNoSourceReason !== undefined) meta.noEditableSourceReason = changes.newNoSourceReason || null;
      // v2 (D90) — topic/geo ride the metadata PUT (registry-validated
      // server-side; topic can change, never clear).
      if (changes.newTopic !== undefined) meta.topic = changes.newTopic;
      if (changes.newGeography !== undefined) meta.geography = changes.newGeography;
      await axios.put(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/metadata`,
        meta,
        { headers }
      );
    }

    onComplete();
  };

  return (
    <>
      <div style={{ display: 'inline-block', marginLeft: 8 }}>
        <button
          className="review-btn"
          style={{
            background: '#fff',
            border: '1px solid #0c5460',
            color: '#0c5460',
            padding: '6px 12px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 14
          }}
          onClick={() => setOpen(true)}
          title="Edit this cycle's metadata (admin / submission manager only)"
        >
          Edit cycle
        </button>
        {warning && <div style={{ color: '#856404', fontSize: 12, marginTop: 4 }}>{warning}</div>}
      </div>
      {open && (
        <RenameDialog
          title="Edit cycle"
          introCopy="Edit this cycle's metadata. Title / Type / Audience changes are audited and rewrite the SharePoint canonical filename for file-based cycles; the lineage link to any predecessor is left intact. Public name and Channel behave exactly like the dashboard's editor."
          submitLabel="Save changes"
          reasonLabel="Reason for change (audited)"
          reasonPlaceholder="e.g. typo in title; wrong audience tagged at submit"
          currentName={cycle.contentItem.name || ''}
          currentType={cycle.contentItem.type || ''}
          currentAudience={cycle.contentItem.audience || ''}
          showExtendedFields
          currentPublicName={(cycle.contentItem as any).publicName || ''}
          currentChannel={cycle.channel || ''}
          currentSourceLink={(cycle.contentItem as any).sourceLink || ''}
          currentNoSourceReason={(cycle.contentItem as any).noEditableSourceReason || ''}
          currentTopic={(cycle.contentItem as any).topic || 'other'}
          currentGeography={(cycle.contentItem as any).geography || 'global'}
          format={(cycle as any).contentType || null}
          getAuthHeaders={getAuthHeaders}
          onSubmit={handleSubmit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default RenameCycleButton;
